import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CircleCheckBig,
  FolderOpen,
  IndianRupee,
  Mail,
  MoreVertical,
  Phone,
  Plus,
  Search,
  Users,
} from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { formatINR, initials, timeAgo } from "@/lib/format";
import { CUSTOMERS_HERO_IMAGE } from "@/lib/stockImages";
import PageHero from "@/components/layout/PageHero";
import KpiCard from "@/components/dashboard/KpiCard";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PAGE_SIZE = 8;
const ALL = "all";

const ENQUIRY_STATUS_LABEL = {
  NEW: "New",
  QUALIFIED: "Follow-up",
  DESIGNING: "Designing",
  QUOTATION: "Quotation",
  WON: "Won",
  LOST: "Lost",
};

const STATUS_STYLE = {
  New: "bg-chart-3/10 text-chart-3",
  "Follow-up": "bg-warning/15 text-[hsl(41_60%_32%)]",
  Designing: "bg-primary/10 text-primary",
  Quotation: "bg-accent/10 text-accent",
  Won: "bg-success/10 text-success",
  Active: "bg-success/10 text-success",
  Lost: "bg-secondary text-muted-foreground",
  Prospect: "bg-secondary text-muted-foreground",
};

const QUOTATION_ACTIVITY = {
  DRAFT: "Quotation drafted",
  SUBMITTED_FOR_APPROVAL: "Awaiting approval",
  CHANGES_REQUIRED: "Changes requested",
  FINALIZED: "Quotation finalized",
  SHARED: "Quotation sent",
};

const CLOSED_PROJECT_STATUSES = ["COMPLETED", "CANCELLED", "CLOSED"];

const latest = (items) =>
  items.reduce((best, i) => (!best || new Date(i.updatedAt) > new Date(best.updatedAt) ? i : best), null);

const groupBy = (items, key) =>
  items.reduce((m, i) => {
    (m[i[key]] ||= []).push(i);
    return m;
  }, {});

// Joins customers with their projects, quotations and enquiries so each row
// can show project count, quoted value, last activity and a pipeline status.
function buildRows(customers, projects, quotations, enquiries) {
  const projectsBy = groupBy(projects, "customerId");
  const quotationsBy = groupBy(quotations, "customerId");
  const enquiriesBy = groupBy(enquiries, "customerId");

  return customers.map((c) => {
    const cProjects = projectsBy[c._id] || [];
    const cQuotations = quotationsBy[c._id] || [];
    const cEnquiries = enquiriesBy[c._id] || [];

    // Only the most recent quotation of each project counts toward the value.
    const latestPerProject = Object.values(groupBy(cQuotations, "projectId")).map(latest);
    const value = latestPerProject.reduce((sum, q) => sum + (q.grandTotal || 0), 0);

    const lastEnquiry = latest(cEnquiries);
    const status = lastEnquiry
      ? ENQUIRY_STATUS_LABEL[lastEnquiry.status] || "Active"
      : cProjects.length > 0
        ? "Active"
        : "Prospect";

    const events = [
      { at: c.createdAt, label: "Customer added" },
      ...cProjects.map((p) => ({ at: p.updatedAt, label: "Project updated" })),
      ...cQuotations.map((q) => ({ at: q.updatedAt, label: QUOTATION_ACTIVITY[q.status] || "Quotation updated" })),
      ...cEnquiries.map((e) => ({
        at: e.updatedAt,
        label: e.status === "NEW" ? "Enquiry created" : `Enquiry moved to ${ENQUIRY_STATUS_LABEL[e.status] || e.status}`,
      })),
    ].filter((e) => e.at);
    const lastActivity = events.reduce((best, e) => (!best || new Date(e.at) > new Date(best.at) ? e : best), null);

    const latestProject = latest(cProjects);
    const converted =
      cEnquiries.some((e) => e.status === "WON") || cQuotations.some((q) => ["FINALIZED", "SHARED"].includes(q.status));

    return {
      ...c,
      projectCount: cProjects.length,
      projectMeta: latestProject ? [latestProject.city, latestProject.bhk].filter(Boolean).join(" · ") : "",
      cities: [...new Set(cProjects.map((p) => p.city).filter(Boolean))],
      value,
      status,
      lastActivity,
      converted,
    };
  });
}

function activityDot(at) {
  const days = (Date.now() - new Date(at).getTime()) / 86400000;
  if (days <= 3) return "bg-success";
  if (days <= 7) return "bg-warning";
  return "bg-muted-foreground/50";
}

function StatusPill({ status }) {
  return (
    <span className={cn("inline-flex rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap", STATUS_STYLE[status])}>
      {status}
    </span>
  );
}

function CustomerAvatar({ name }) {
  return (
    <span className="h-11 w-11 rounded-full bg-secondary text-foreground/80 font-serif text-sm flex items-center justify-center shrink-0">
      {initials(name)}
    </span>
  );
}

function RowMenu({ c }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${c.name}`}
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => navigate(`/customers/${c._id}`)}>View details</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/projects?customerId=${c._id}`)}>View projects</DropdownMenuItem>
        {c.phone && (
          <DropdownMenuItem asChild>
            <a href={`tel:${c.phone}`}>Call {c.phone}</a>
          </DropdownMenuItem>
        )}
        {c.email && (
          <DropdownMenuItem asChild>
            <a href={`mailto:${c.email}`}>Email customer</a>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ContactLines({ c }) {
  return (
    <div className="space-y-1 text-sm text-foreground/85 min-w-0">
      <p className="flex items-center gap-2 truncate">
        <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> {c.phone || "—"}
      </p>
      <p className="flex items-center gap-2 truncate">
        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> <span className="truncate">{c.email || "—"}</span>
      </p>
    </div>
  );
}

function AddCustomerDialog({ open, setOpen }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });

  const createMutation = useMutation({
    mutationFn: (payload) => client.post("/customers", payload),
    onSuccess: () => {
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", address: "" });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="add-customer-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add Customer</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Address</Label><Input data-testid="customer-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <Button
            data-testid="customer-submit-button"
            className="w-full"
            disabled={!form.name || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [cityFilter, setCityFilter] = useState(ALL);
  const [page, setPage] = useState(1);

  const canSeeProjects = hasPermission("project:manage");
  const canSeeQuotations = hasPermission("quotation:create");
  const canSeeEnquiries = hasPermission("enquiry:manage");

  const { data: customers = [], isLoading } = useQuery({ queryKey: ["customers"], queryFn: () => client.get("/customers").then(unwrap) });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => client.get("/projects").then(unwrap),
    enabled: canSeeProjects,
  });
  const { data: quotations = [] } = useQuery({
    queryKey: ["quotations-all"],
    queryFn: () => client.get("/quotations").then(unwrap),
    enabled: canSeeQuotations,
  });
  const { data: enquiries = [] } = useQuery({
    queryKey: ["enquiries"],
    queryFn: () => client.get("/enquiries").then(unwrap),
    enabled: canSeeEnquiries,
  });

  const rows = useMemo(
    () =>
      buildRows(customers, projects, quotations, enquiries).sort(
        (a, b) => new Date(b.lastActivity?.at || 0) - new Date(a.lastActivity?.at || 0),
      ),
    [customers, projects, quotations, enquiries],
  );

  const statuses = useMemo(() => [...new Set(rows.map((r) => r.status))], [rows]);
  const cities = useMemo(() => [...new Set(rows.flatMap((r) => r.cities))].sort(), [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!term || [r.name, r.phone, r.email].some((v) => v?.toLowerCase().includes(term))) &&
        (statusFilter === ALL || r.status === statusFilter) &&
        (cityFilter === ALL || r.cities.includes(cityFilter)),
    );
  }, [rows, search, statusFilter, cityFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filtersActive = search || statusFilter !== ALL || cityFilter !== ALL;

  const now = new Date();
  const addedThisMonth = customers.filter((c) => {
    const d = new Date(c.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const openProjects = projects.filter((p) => !CLOSED_PROJECT_STATUSES.includes(p.status));
  const totalValue = rows.reduce((sum, r) => sum + r.value, 0);
  const convertedCount = rows.filter((r) => r.converted).length;

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };
  const clearFilters = () => {
    setSearch("");
    setStatusFilter(ALL);
    setCityFilter(ALL);
    setPage(1);
  };

  return (
    <div data-testid="customers-page" className="space-y-6">
      <div>
        <PageHero
          image={CUSTOMERS_HERO_IMAGE}
          tagline="Relationships that create beautiful spaces"
          title="Customers"
          subtitle="Every project starts with a customer."
          action={
            <Button data-testid="add-customer-button" className="rounded-full px-5" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Customer
            </Button>
          }
        />

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6 xl:-mt-14 xl:px-4">
          <KpiCard
            testId="kpi-total-customers"
            icon={Users}
            tone="green"
            label="Total Customers"
            value={customers.length}
            sublabel={`+${addedThisMonth} added this month`}
            sublabelTone="text-success"
          />
          {canSeeProjects && (
            <KpiCard
              testId="kpi-customer-projects"
              to="/projects"
              icon={FolderOpen}
              tone="terracotta"
              label="Active Projects"
              value={openProjects.length}
              sublabel={(() => {
                const n = new Set(openProjects.map((p) => p.customerId)).size;
                return `across ${n} customer${n === 1 ? "" : "s"}`;
              })()}
            />
          )}
          {canSeeQuotations && (
            <KpiCard
              testId="kpi-customer-quotations"
              to="/quotations"
              icon={IndianRupee}
              tone="gold"
              label="Quoted Value"
              value={formatINR(totalValue)}
              sublabel={`${quotations.length} quotation${quotations.length === 1 ? "" : "s"}`}
            />
          )}
          <KpiCard
            testId="kpi-converted"
            icon={CircleCheckBig}
            tone="green"
            label="Converted"
            value={convertedCount}
            sublabel={customers.length ? `${Math.round((convertedCount / customers.length) * 100)}% of customers` : "—"}
          />
        </div>
      </div>

      <section className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-border">
          <div className="relative flex-1 lg:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="customers-search-input"
              value={search}
              onChange={(e) => resetPage(setSearch)(e.target.value)}
              placeholder="Search customers by name, phone, email…"
              className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={statusFilter} onValueChange={resetPage(setStatusFilter)}>
              <SelectTrigger data-testid="customers-status-filter" className="w-[160px] rounded-lg bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {cities.length > 0 && (
              <Select value={cityFilter} onValueChange={resetPage(setCityFilter)}>
                <SelectTrigger data-testid="customers-city-filter" className="w-[160px] rounded-lg bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All cities</SelectItem>
                  {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {filtersActive && (
              <button data-testid="customers-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline px-2">
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/50 text-muted-foreground text-[11px] uppercase tracking-wider text-left">
                <th className="py-3 pl-5 pr-3 font-semibold">Customer</th>
                <th className="py-3 px-3 font-semibold">Contact</th>
                <th className="py-3 px-3 font-semibold">Projects</th>
                {canSeeQuotations && <th className="py-3 px-3 font-semibold">Total value</th>}
                <th className="py-3 px-3 font-semibold">Last activity</th>
                <th className="py-3 px-3 font-semibold">Status</th>
                <th className="py-3 pl-3 pr-5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pageRows.map((c) => (
                <tr
                  key={c._id}
                  data-testid={`customer-row-${c._id}`}
                  onClick={() => navigate(`/customers/${c._id}`)}
                  className="hover:bg-secondary/40 cursor-pointer transition-colors"
                >
                  <td className="py-3.5 pl-5 pr-3">
                    <div className="flex items-center gap-3">
                      <CustomerAvatar name={c.name} />
                      <div className="min-w-0">
                        <Link
                          to={`/customers/${c._id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-foreground hover:text-primary"
                          data-testid={`customer-name-link-${c._id}`}
                        >
                          {c.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          Since {new Date(c.createdAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3.5 px-3 max-w-[220px]"><ContactLines c={c} /></td>
                  <td className="py-3.5 px-3">
                    <div className="flex items-start gap-2.5">
                      <FolderOpen className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
                      <div>
                        <p className="font-medium text-foreground whitespace-nowrap">
                          {c.projectCount} Project{c.projectCount === 1 ? "" : "s"}
                        </p>
                        {c.projectMeta && <p className="text-xs text-muted-foreground whitespace-nowrap">{c.projectMeta}</p>}
                      </div>
                    </div>
                  </td>
                  {canSeeQuotations && (
                    <td className="py-3.5 px-3 font-serif text-base text-foreground whitespace-nowrap tabular-nums">
                      {c.value ? `₹${c.value.toLocaleString("en-IN")}` : <span className="text-muted-foreground font-sans text-sm">—</span>}
                    </td>
                  )}
                  <td className="py-3.5 px-3">
                    {c.lastActivity && (
                      <div className="flex items-start gap-2">
                        <span className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", activityDot(c.lastActivity.at))} />
                        <div>
                          <p className="text-foreground whitespace-nowrap">{timeAgo(c.lastActivity.at)}</p>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">{c.lastActivity.label}</p>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="py-3.5 px-3"><StatusPill status={c.status} /></td>
                  <td className="py-3.5 pl-3 pr-5">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link
                        to={`/projects?customerId=${c._id}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`customer-view-projects-${c._id}`}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-accent hover:border-accent transition-colors"
                      >
                        View Projects <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                      <RowMenu c={c} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-border">
          {pageRows.map((c) => (
            <li key={c._id} data-testid={`customer-card-${c._id}`} className="p-4" onClick={() => navigate(`/customers/${c._id}`)}>
              <div className="flex items-start gap-3">
                <CustomerAvatar name={c.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-foreground truncate">{c.name}</p>
                    <StatusPill status={c.status} />
                  </div>
                  <div className="mt-2"><ContactLines c={c} /></div>
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {c.projectCount} project{c.projectCount === 1 ? "" : "s"}
                      {canSeeQuotations && c.value ? ` · ${formatINR(c.value)}` : ""}
                    </span>
                    {c.lastActivity && <span>{timeAgo(c.lastActivity.at)}</span>}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {!isLoading && filtered.length === 0 && (
          <div className="py-14 text-center">
            <p className="text-sm text-muted-foreground">
              {customers.length === 0 ? "No customers yet. Add your first one to get started." : "No customers match these filters."}
            </p>
            {filtersActive && (
              <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>
            )}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} customer
              {filtered.length === 1 ? "" : "s"}
            </p>
            <Pagination testId="customers-pagination" page={currentPage} pageCount={pageCount} onChange={setPage} />
          </div>
        )}
      </section>

      <AddCustomerDialog open={open} setOpen={setOpen} />
    </div>
  );
}
